import AutoFixHigh from "@mui/icons-material/AutoFixHigh";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import FileUpload from "@mui/icons-material/FileUpload";
import FolderOutlined from "@mui/icons-material/FolderOutlined";
import { Alert, Box, Button, CircularProgress, IconButton, Stack, Typography } from "@mui/material";
import { RichTreeView } from "@mui/x-tree-view";
import { useCallback, useEffect, useMemo, useReducer, type FC } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import { AssetCount } from "../../modules/assets/asset-count";
import { AssetGrid } from "../../modules/assets/asset-grid";
import { AssetPreviewModal } from "../../modules/assets/asset-preview-modal";
import { ContentLibraryFilter } from "../../modules/assets/components/content-library-filter";
import { CustomTreeItem } from "../../modules/assets/components/custom-tree-item";
import { FolderBreadcrumb } from "../../modules/assets/components/folder-breadcrumb";
import { ContentTypeChips } from "../../modules/assets/content-type-chips";
import { EmptyState } from "../../modules/assets/empty-state";
import { useAssets } from "../../modules/assets/hooks/use-assets";
import { useFolders } from "../../modules/assets/hooks/use-folders";
import {
  contentLibraryReducer,
  filterAssets,
  initialState,
} from "../../modules/assets/reducer/reducer";
import type { Asset } from "../../modules/assets/server/digital-asset-service";
import { TagChips } from "../../modules/assets/tag-chips";
import { UploadNewFileModal } from "../../modules/assets/upload-new-file-modal";
import { transformFoldersToTreeItems } from "../../modules/assets/utils";
import { HeaderBar } from "../../modules/generic/components/header-bar";
import { useAccount } from "../../modules/generic/hooks/use-account";
import { Page } from "../../modules/layout/components/page";
import { PageContent } from "../../modules/layout/components/page-content";
import { SearchField } from "../../modules/search/search-field";
import { useFlag } from "../../server/optimizely";
import { useDialog } from "../../utils/hooks/use-dialog";
import { contentTypes as allContentTypes } from "./content-types";

const SIDEBAR_WIDTH = 280;

// TODO: This will need changing once the tags are fetched from the backend
function extractAvailableTags(assets: Asset[]): string[] {
  return Array.from(
    new Set(
      assets.filter((asset) => asset.tags && asset.tags.length > 0).flatMap((asset) => asset.tags!),
    ),
  ).sort();
}

export const ContentLibraryPageNewInner: FC = () => {
  const { t } = useTranslation();
  const [state, dispatch] = useReducer(contentLibraryReducer, initialState);
  const [searchParams, setSearchParams] = useSearchParams();
  const [video_uploads] = useFlag("video_uploads");

  const previewModal = useDialog();
  const uploadModal = useDialog();

  const { data, loading, error, reload } = useAssets();
  const { data: foldersData, loading: foldersLoading, error: foldersError } = useFolders();
  const account = useAccount();

  useEffect(() => {
    const formats = searchParams.has("format") ? searchParams.getAll("format") : [];
    const tags = searchParams.has("tags") ? searchParams.getAll("tags") : [];
    const previewAssetId = searchParams.get("preview");

    dispatch({ type: "SYNC_FROM_URL", payload: { contentTypes: formats, tags, previewAssetId } });
  }, [searchParams]);

  useEffect(() => {
    if (state.previewAssetId && !previewModal.open) {
      previewModal.handleOpen();
    } else if (!state.previewAssetId && previewModal.open) {
      previewModal.handleClose();
    }
  }, [state.previewAssetId, previewModal]);

  const selectedAsset = useMemo(() => {
    if (!state.previewAssetId || loading || !data) return null;
    return data?.find((x) => x.id === state.previewAssetId) ?? null;
  }, [state.previewAssetId, data, loading]);

  const treeItems = useMemo(() => {
    if (!foldersData || foldersError || foldersLoading) return [];
    return transformFoldersToTreeItems(foldersData ?? [], t("Content Library"));
  }, [foldersData, foldersError, foldersLoading, t]);

  // TODO: This will need changing once the tags are fetched from the backend
  const availableTags = useMemo(() => {
    if (!data) return [];
    return extractAvailableTags(data);
  }, [data]);

  // Compute filtered assets based on current state
  const filteredData = useMemo(() => {
    if (!data) return null;
    return filterAssets(data, state, treeItems);
  }, [data, state, treeItems]);

  const handleFolderSelect = useCallback((folderId: string): void => {
    dispatch({ type: "SELECT_FOLDER", payload: folderId });
  }, []);

  const handleSelectAsset = useCallback(
    (asset: Asset) => {
      dispatch({ type: "SET_PREVIEW_ASSET_ID", payload: asset.id });
      const params = new URLSearchParams(searchParams);
      params.set("preview", asset.id);
      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );

  const handleClosePreviewModal = useCallback(() => {
    dispatch({ type: "SET_PREVIEW_ASSET_ID", payload: null });
    const params = new URLSearchParams(searchParams);
    params.delete("preview");
    setSearchParams(params);
  }, [searchParams, setSearchParams]);

  const handleDeleted = useCallback(() => {
    handleClosePreviewModal();
    reload();
  }, [handleClosePreviewModal, reload]);

  const handleTreeItemClick = useCallback(
    (_event: React.MouseEvent, itemId: string): void => {
      handleFolderSelect(itemId);
    },
    [handleFolderSelect],
  );

  const handleRemoveContentTypeFilter = useCallback(
    (contentType: string) => {
      dispatch({ type: "REMOVE_CONTENT_TYPE_FILTER", payload: contentType });

      // Update URL
      const params = new URLSearchParams(searchParams);
      const newValues = params.getAll("format").filter((c) => c !== contentType);
      params.delete("format");
      newValues.forEach((val) => params.append("format", val));
      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );

  const handleRemoveTagFilter = useCallback(
    (tag: string) => {
      dispatch({ type: "REMOVE_TAG_FILTER", payload: tag });

      // Update URL
      const params = new URLSearchParams(searchParams);
      const newValues = params.getAll("tags").filter((tagValue) => tagValue !== tag);
      params.delete("tags");
      newValues.forEach((val) => params.append("tags", val));
      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );

  const handleApplyFilter = useCallback(() => {
    dispatch({ type: "APPLY_FILTERS" });

    // Update URL with new filters
    const params = new URLSearchParams();
    state.selectedContentTypes.forEach((c) => params.append("format", c));
    state.selectedTags.forEach((tag) => params.append("tags", tag));
    setSearchParams(params);
  }, [state.selectedContentTypes, state.selectedTags, setSearchParams]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch({ type: "SET_SEARCH_TEXT", payload: e.target.value });
  }, []);

  const handleSidebarToggle = useCallback((open: boolean) => {
    dispatch({ type: "SET_SIDEBAR_OPEN", payload: open });
  }, []);

  const handleExpandedItemsChange = useCallback(
    (_event: React.SyntheticEvent | null, itemIds: string[]) => {
      dispatch({ type: "SET_EXPANDED_ITEMS", payload: itemIds });
    },
    [],
  );

  const handleSetSelectedContentTypes = useCallback(
    (contentTypes: string[] | ((prev: string[]) => string[])) => {
      if (typeof contentTypes === "function") {
        // For functional updates, we'd need current state - but since ContentLibraryFilter
        // only passes arrays directly, we can handle this case if needed
        return;
      }
      dispatch({ type: "SET_SELECTED_CONTENT_TYPES", payload: contentTypes });
    },
    [],
  );

  const handleSetSelectedTags = useCallback((tags: string[] | ((prev: string[]) => string[])) => {
    if (typeof tags === "function") {
      return;
    }
    dispatch({ type: "SET_SELECTED_TAGS", payload: tags });
  }, []);

  return (
    <>
      <Page
        title={t("Content Library | ChangeEngine")}
        sx={{ height: "100%", display: "flex", flexDirection: "column" }}
      >
        <HeaderBar navBurger>
          <Typography variant="body1" fontWeight={700} fontSize={16}>
            {t("Content Library")}
          </Typography>
          <Box>
            <Button
              onClick={uploadModal.handleOpen}
              variant="outlined"
              color="primary"
              data-analytics-id="content-library-upload-file"
              startIcon={<FileUpload />}
              sx={{ mr: 2 }}
            >
              {t("Upload file")}
            </Button>
            <Button
              variant="contained"
              color="primary"
              href="/"
              data-analytics-id="content-library-create-content-cta"
              startIcon={<AutoFixHigh />}
            >
              {t("Create Content")}
            </Button>
          </Box>
        </HeaderBar>
        <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
          <Box
            sx={{
              width: state.sidebarOpen ? SIDEBAR_WIDTH : 60,
              flexShrink: 0,
              bgcolor: state.sidebarOpen ? "background.default" : "transparent",
              transition: "width 0.3s ease-in-out, background-color 0.3s ease-in-out",
              overflow: "hidden",
            }}
          >
            {state.sidebarOpen ? (
              <Stack
                sx={{
                  width: SIDEBAR_WIDTH,
                  px: "16px",
                  pt: "16px",
                }}
                gap={2}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight={500}
                    fontSize={16}
                    color="text.primary"
                  >
                    {t("Folders")}
                  </Typography>
                  <ChevronLeft
                    sx={{ cursor: "pointer" }}
                    onClick={() => handleSidebarToggle(false)}
                  />
                </Box>
                <RichTreeView
                  items={treeItems}
                  onItemClick={handleTreeItemClick}
                  selectedItems={state.selectedTreeItemId}
                  expandedItems={state.expandedItems}
                  onExpandedItemsChange={handleExpandedItemsChange}
                  slots={{ item: CustomTreeItem }}
                  expansionTrigger="iconContainer"
                />
              </Stack>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  pt: "16px",
                  pl: "16px",
                }}
              >
                <Box
                  sx={{
                    borderRadius: "4px",
                    border: "1px solid",
                    borderColor: "neutral.300",
                    p: "8px",
                    pb: "4px",
                    color: "text.secondary",
                  }}
                >
                  <FolderOutlined
                    sx={{ cursor: "pointer" }}
                    onClick={() => handleSidebarToggle(true)}
                    width={24}
                    height={24}
                  />
                </Box>
              </Box>
            )}
          </Box>
          <Box sx={{ flex: 1 }}>
            <PageContent maxWidth="xl">
              {loading && <CircularProgress />}
              {error && (
                <Alert severity="error">
                  {t("Sorry, there was a problem retrieving your content. Please try again later")}
                </Alert>
              )}
              {filteredData && (
                <Stack>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <FolderBreadcrumb
                      treeItems={treeItems}
                      selectedTreeItemId={state.selectedTreeItemId}
                      onFolderSelect={handleFolderSelect}
                    />
                    <Stack direction="row" alignItems="center" gap={1}>
                      <Button
                        variant="text"
                        color="primary"
                        data-analytics-id="content-library-move-folder"
                      >
                        {t("Move folder")}
                      </Button>
                      <Button
                        variant="text"
                        color="primary"
                        data-analytics-id="content-library-create-sub-folder"
                      >
                        {t("Create sub-folder")}
                      </Button>
                      <IconButton data-analytics-id="content-library-delete-folder">
                        <DeleteOutline color="error" sx={{ fontSize: 20 }} />
                      </IconButton>
                    </Stack>
                  </Box>
                  <Stack direction="row" justifyContent={"space-between"} pb={2}>
                    <Box flexGrow={1} mr={2}>
                      <SearchField
                        label="Search"
                        defaultValue={state.searchText}
                        onChange={handleSearchChange}
                        fullWidth
                      />
                    </Box>
                    <ContentLibraryFilter
                      handleApplyFilter={handleApplyFilter}
                      selectedContentTypes={state.selectedContentTypes}
                      setSelectedContentTypes={handleSetSelectedContentTypes}
                      contentTypes={allContentTypes}
                      selectedTags={state.selectedTags}
                      setSelectedTags={handleSetSelectedTags}
                      availableTags={availableTags}
                    />
                  </Stack>
                  <Stack direction="row" justifyContent={"space-between"}>
                    <AssetCount assets={filteredData} searchText={state.searchText} />
                  </Stack>
                  <Stack gap={1} sx={{ mb: 2 }}>
                    <ContentTypeChips
                      onDelete={handleRemoveContentTypeFilter}
                      contentTypes={state.appliedContentTypes}
                    />
                    <TagChips onDelete={handleRemoveTagFilter} tags={state.appliedTags} />
                  </Stack>
                  <AssetGrid
                    assets={filteredData}
                    onClick={handleSelectAsset}
                    designer={account.data?.designer}
                  />
                </Stack>
              )}
              {data && data.length === 0 && <EmptyState />}
            </PageContent>
          </Box>
        </Box>
      </Page>
      <AssetPreviewModal
        asset={selectedAsset}
        open={previewModal.open}
        onClose={handleClosePreviewModal}
        onChange={reload}
        onDeleted={handleDeleted}
        onViewFileClick={(asset) => {
          handleSelectAsset(asset);
        }}
      />
      <UploadNewFileModal
        open={uploadModal.open}
        onOpen={uploadModal.handleOpen}
        onClose={uploadModal.handleClose}
        onCancel={uploadModal.handleClose}
        onChange={reload}
        onViewFileClick={(asset) => {
          handleSelectAsset(asset);
        }}
        videoUploads={video_uploads}
      />
    </>
  );
};
